import React from "react";
import PropTypes from "prop-types";
import { Link, useLocation } from "react-router-dom";
import { getSection } from "../config/sections";
import { getWatermarkIconForRoute } from "../config/watermarkIcons";
import "./TitlePages.css";

// Función auxiliar para normalizar texto eliminando mayúsculas, acentos y espacios repetidos
const normalizeText = (str) => {
  if (!str || typeof str !== "string") return "";
  return str
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ");
};

const TitlePages = ({
  section = "",
  title = "",
  subsections = [],
  // Propiedades legadas o alternativas
  parent = null,
  parentSection = "",
  parentUrl = "",
  description = "",
  subTitle = "",
  path = null,
  items = null,
  breadcrumbs = null,
  breadcrumb = null,
  hideBreadcrumbs = false,
  className = "",
  hideTitle = false,
  variant = "banner",
  theme = "",
  icon = null,
  watermarkIcon = null,
}) => {
  const location = useLocation();
  const currentPath = location && location.pathname ? location.pathname : "";

  // 1. Resolver el título de la página actual (displayTitle)
  // Regla: El título grande muestra SIEMPRE el nombre de la página actual.
  let displayTitle = (title || "").trim();
  let displayDesc = "";

  if (subTitle && subTitle.trim() !== "") {
    displayTitle = subTitle.trim();
    if (description && description.trim() !== "" && description.trim() !== displayTitle) {
      displayDesc = description.trim();
    }
  } else if (description && description.trim() !== "") {
    displayDesc = description.trim();
  }

  // Si no se proporciona un título principal ni subtítulo:
  if (!displayTitle || displayTitle === "") {
    if (!displayDesc || displayDesc === "") {
      return null;
    }
    return (
      <div className={`page-subtitle-block ${className}`.trim()}>
        <h2 className="page-subtitle-title">{displayDesc}</h2>
        <div className="page-subtitle-line" aria-hidden="true" />
      </div>
    );
  }

  // Si se ocultó el título explícitamente
  if (hideTitle) {
    return null;
  }

  // 2. Resolver sección padre (sectionObj)
  let sectionKeyOrPath = section || parent || parentSection;
  let sectionObj = null;

  if (typeof sectionKeyOrPath === "object" && sectionKeyOrPath !== null) {
    sectionObj = {
      key: sectionKeyOrPath.key || "",
      name: sectionKeyOrPath.name || sectionKeyOrPath.label || "",
      url: sectionKeyOrPath.url || "",
    };
  } else if (typeof sectionKeyOrPath === "string" && sectionKeyOrPath.trim() !== "") {
    sectionObj = getSection(sectionKeyOrPath);
    if (!sectionObj && (parent || parentSection)) {
      sectionObj = {
        key: "",
        name: parent || parentSection,
        url: parentUrl || "",
      };
    }
  }

  // Si no se pasó explícito, intentar por la ruta actual
  if (!sectionObj && currentPath) {
    sectionObj = getSection(currentPath);
  }

  // Si aún no se resolvió pero title original correspondía a una sección registrada
  if (!sectionObj && title && title.trim() !== "") {
    sectionObj = getSection(title);
  }

  // 3. Normalizar subsecciones intermedias (si existen)
  let intermediateLevels = [];
  if (Array.isArray(subsections)) {
    intermediateLevels = subsections;
  } else if (subsections) {
    intermediateLevels = [subsections];
  }

  const normalizedSubsections = intermediateLevels
    .filter(Boolean)
    .map((item) => {
      if (typeof item === "string") {
        return { label: item, url: null };
      }
      return {
        label: item.name || item.label || item.title || "",
        url: item.url || item.path || null,
      };
    })
    .filter((sub) => sub.label && sub.label.trim() !== "");

  // 4. Construir la lista de migas de pan (breadcrumb items)
  // - El breadcrumb muestra SOLO los niveles ANTERIORES a la página actual, sin incluirla.
  // - En páginas de 2° nivel (ej. /Transparencia): breadcrumb = ["Inicio"], título = "Transparencia".
  // - En páginas de 3° nivel (ej. /infografias): breadcrumb = ["Inicio", "Transparencia"], título = "Infografías".
  // - En páginas como Artículo 63 (2022): breadcrumb = ["Inicio", "Transparencia"], título = "Artículo 63 (2022)".
  // - Si hay más niveles intermedios, se incluyen todos menos el último.
  // - Todos los elementos que quedan son enlaces a su página (ya que ninguno es la página actual).
  // - REGLA DE RESPALDO: NUNCA debe aparecer el mismo texto en el breadcrumb y en el título (sin importar mayúsculas, acentos ni espacios extra).
  // - Ningún chevron sobrante al final.
  const breadcrumbItems = [];

  // Verificar si se pasó una lista manual de migas de pan (items, path, breadcrumbs, breadcrumb)
  const rawManual = items || path || breadcrumbs || breadcrumb;
  let manualItems = [];
  if (Array.isArray(rawManual)) {
    manualItems = rawManual;
  } else if (rawManual) {
    manualItems = [rawManual];
  }

  const normalizedManual = manualItems
    .filter(Boolean)
    .map((item) => {
      if (typeof item === "string") return { label: item.trim(), url: null };
      return {
        label: (item.name || item.label || item.title || "").trim(),
        url: item.url || item.path || null,
      };
    })
    .filter((item) => item.label && item.label !== "");

  if (normalizedManual.length > 0) {
    // Si la lista manual incluye "Inicio", conservarla; si no, agregarla al principio
    const startsWithHome =
      normalizeText(normalizedManual[0].label) === "inicio";
    if (!startsWithHome) {
      breadcrumbItems.push({ label: "Inicio", url: "/" });
    }

    normalizedManual.forEach((mItem) => {
      breadcrumbItems.push(mItem);
    });

    // Si el último elemento de la lista manual coincide con el título actual, omitirlo
    if (breadcrumbItems.length > 1) {
      const last = breadcrumbItems[breadcrumbItems.length - 1];
      if (normalizeText(last.label) === normalizeText(displayTitle)) {
        breadcrumbItems.pop();
      }
    }
  } else {
    // Generación automática a partir de la jerarquía de secciones del sitio
    breadcrumbItems.push({
      label: "Inicio",
      url: "/",
    });

    // Nivel de sección padre: se agrega si no coincide con la página actual
    if (sectionObj && sectionObj.name && sectionObj.name.trim() !== "") {
      const isSectionCurrent =
        normalizeText(sectionObj.name) === normalizeText(displayTitle);
      if (!isSectionCurrent) {
        breadcrumbItems.push({
          label: sectionObj.name.trim(),
          url: sectionObj.url || null,
        });
      }
    }

    // Subsecciones intermedias (si existen)
    normalizedSubsections.forEach((sub) => {
      const isCurrent = normalizeText(sub.label) === normalizeText(displayTitle);
      const isSection =
        sectionObj &&
        sectionObj.name &&
        normalizeText(sub.label) === normalizeText(sectionObj.name);
      if (!isCurrent && !isSection) {
        breadcrumbItems.push({
          label: sub.label.trim(),
          url: sub.url || null,
        });
      }
    });
  }

  // Regla de respaldo obligatoria: NUNCA debe aparecer el mismo texto en el breadcrumb y en el título
  // (sin importar mayúsculas, acentos ni espacios extra)
  const normTitle = normalizeText(displayTitle);
  const finalBreadcrumbs = breadcrumbItems.filter(
    (item) => normalizeText(item.label) !== normTitle
  );

  if (finalBreadcrumbs.length === 0) {
    finalBreadcrumbs.push({ label: "Inicio", url: "/" });
  }

  // Si se solicita la variante de banner (ej. encabezado tipo tarjeta de Transparencia)
  const isBanner =
    variant === "banner" ||
    variant === "banner-light" ||
    (className && (className.includes("transp-banner-hero") || className.includes("page-banner")));

  const isLight =
    theme === "light" ||
    variant === "banner-light" ||
    (className && className.includes("page-banner--light"));

  const bannerClasses = `transp-banner-hero page-banner ${
    isLight ? "page-banner--light" : ""
  } ${className}`.trim();

  const WatermarkComponent = getWatermarkIconForRoute(
    currentPath,
    sectionObj?.key || section || parent || parentSection || "",
    displayTitle || title,
    watermarkIcon || icon
  );

  if (isBanner) {
    return (
      <header className={bannerClasses} aria-label="Encabezado de sección">
        {/* Contenido: Migas de pan ARRIBA, Título ABAJO, ambos alineados a la izquierda */}
        <div className="transp-banner-content">
          {!hideBreadcrumbs && (
            <nav aria-label="Migas de pan" className="transp-banner-breadcrumbs">
              <div className="transp-breadcrumb-pill crumbs-pill">
                <div className="transp-breadcrumb-pill-inner">
                  <ol className="transp-breadcrumb-list">
                    {finalBreadcrumbs.map((item, idx) => {
                      const isLast = idx === finalBreadcrumbs.length - 1;
                      const isHome = idx === 0 && item.label === "Inicio";
                      return (
                        <React.Fragment key={idx}>
                          <li className="transp-breadcrumb-item">
                            {item.url ? (
                              item.url.startsWith("http") ? (
                                <a
                                  href={item.url}
                                  className="transp-breadcrumb-link"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {isHome && (
                                    <svg
                                      className="transp-breadcrumb-home-icon"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="1.8"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      width="18"
                                      height="18"
                                      aria-hidden="true"
                                      focusable="false"
                                    >
                                      <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z" />
                                      <polyline points="9 21 9 12 15 12 15 21" />
                                    </svg>
                                  )}
                                  <span>{item.label}</span>
                                </a>
                              ) : (
                                <Link to={item.url} className="transp-breadcrumb-link">
                                  {isHome && (
                                    <svg
                                      className="transp-breadcrumb-home-icon"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="1.8"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      width="18"
                                      height="18"
                                      aria-hidden="true"
                                      focusable="false"
                                    >
                                      <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z" />
                                      <polyline points="9 21 9 12 15 12 15 21" />
                                    </svg>
                                  )}
                                  <span>{item.label}</span>
                                </Link>
                              )
                            ) : (
                              <span className="transp-breadcrumb-link">
                                {isHome && (
                                  <svg
                                    className="transp-breadcrumb-home-icon"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    width="18"
                                    height="18"
                                    aria-hidden="true"
                                    focusable="false"
                                  >
                                    <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z" />
                                    <polyline points="9 21 9 12 15 12 15 21" />
                                  </svg>
                                )}
                                <span>{item.label}</span>
                              </span>
                            )}
                          </li>
                          {!isLast && (
                            <li className="transp-breadcrumb-separator sep" aria-hidden="true">
                              <svg
                                className="transp-breadcrumb-chevron"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                width="14"
                                height="14"
                                aria-hidden="true"
                                focusable="false"
                              >
                                <polyline points="9 18 15 12 9 6" />
                              </svg>
                            </li>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </ol>
                </div>
              </div>
            </nav>
          )}

          {displayTitle && (
            <div className="transp-banner-title-container">
              <h1 className="transp-banner-title" aria-current="page">
                {displayTitle}
              </h1>
            </div>
          )}

          {displayDesc && displayDesc.trim() !== "" && (
            <p className="transp-banner-description">{displayDesc}</p>
          )}
        </div>

        {/* Ícono de marca de agua dinámico por sección */}
        <div className="transp-banner-watermark page-banner-watermark" aria-hidden="true">
          {React.isValidElement(WatermarkComponent) ? (
            WatermarkComponent
          ) : (
            <WatermarkComponent size={150} strokeWidth={1.3} />
          )}
        </div>
      </header>
    );
  }

  return (
    <header className={`page-hero ${className}`.trim()}>
      {/* Círculo radial blur detrás del título */}
      <div className="page-hero-glow" aria-hidden="true"></div>

      <div className="page-hero-container">
        {/* 1. Migas de pan generadas automáticamente */}
        {!hideBreadcrumbs && (
          <nav aria-label="Migas de pan" className="page-hero-breadcrumbs">
            <ol className="page-hero-breadcrumbs-list">
              {finalBreadcrumbs.map((item, idx) => {
                const isLast = idx === finalBreadcrumbs.length - 1;
                return (
                  <React.Fragment key={idx}>
                    <li className="page-hero-breadcrumb-item">
                      {item.url ? (
                        item.url.startsWith("http") ? (
                          <a
                            href={item.url}
                            className="page-hero-breadcrumb-link"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {item.label}
                          </a>
                        ) : (
                          <Link to={item.url} className="page-hero-breadcrumb-link">
                            {item.label}
                          </Link>
                        )
                      ) : (
                        <span className="page-hero-breadcrumb-link">{item.label}</span>
                      )}
                    </li>
                    {!isLast && (
                      <span className="page-hero-breadcrumb-separator" aria-hidden="true">
                        &gt;
                      </span>
                    )}
                  </React.Fragment>
                );
              })}
            </ol>
          </nav>
        )}

        {/* 2. Título h1 directamente bajo las migas */}
        {displayTitle && (
          <div className="page-hero-title-container">
            <h1 className="page-hero-title" aria-current="page">
              {displayTitle}
            </h1>
          </div>
        )}

        {/* Línea de acento proporcional bajo el título */}
        <div className="page-hero-accent-line" aria-hidden="true"></div>

        {/* Descripción corta opcional */}
        {displayDesc && displayDesc.trim() !== "" && (
          <p className="page-hero-description">{displayDesc}</p>
        )}
      </div>
    </header>
  );
};

export const PageHero = TitlePages;
export default TitlePages;

TitlePages.propTypes = {
  section: PropTypes.string,
  title: PropTypes.string,
  subsections: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          name: PropTypes.string,
          label: PropTypes.string,
          title: PropTypes.string,
          url: PropTypes.string,
          path: PropTypes.string,
        }),
      ])
    ),
    PropTypes.string,
    PropTypes.shape({
      name: PropTypes.string,
      label: PropTypes.string,
      title: PropTypes.string,
      url: PropTypes.string,
      path: PropTypes.string,
    }),
  ]),
  parent: PropTypes.oneOfType([
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      url: PropTypes.string,
    }),
    PropTypes.string,
  ]),
  parentSection: PropTypes.string,
  parentUrl: PropTypes.string,
  description: PropTypes.string,
  subTitle: PropTypes.string,
  hideBreadcrumbs: PropTypes.bool,
  className: PropTypes.string,
  hideTitle: PropTypes.bool,
  variant: PropTypes.string,
  theme: PropTypes.string,
  icon: PropTypes.oneOfType([PropTypes.elementType, PropTypes.string, PropTypes.node]),
  watermarkIcon: PropTypes.oneOfType([PropTypes.elementType, PropTypes.string, PropTypes.node]),
};

export const SubTitle = ({ subTitle = "", title = "", className = "" }) => {
  const text = subTitle || title;
  if (!text || text.trim() === "") return null;
  return (
    <div className={`page-subtitle-block ${className}`.trim()}>
      <h2 className="page-subtitle-title">{text}</h2>
      <div className="page-subtitle-line" aria-hidden="true" />
    </div>
  );
};

export const Title = ({ title = "", className = "" }) => {
  if (!title || title.trim() === "") return null;
  return <TitlePages title={title} className={className} />;
};
